/**
 * This module provides non-reactive getter-like functions which are then called
 * from the real getters. This was done to optimize the sport offer event grid.
 * Vuex reactivity added to all objects ended up being too big of a performance
 * hit.
 */
import { MaintenanceType } from '@superbet-group/web.types';
import {
    last as _last,
    sortBy as _sortBy,
    get as _get,
} from 'lodash';
import {
    map,
    flow,
    uniqBy,
    orderBy,
} from 'lodash/fp';

import { IState } from '@app/store';
import {
    INode,
    orderTree,
    getBreadcrumbIds,
    createOrUpdateTreeNode,
    buildTreeFromMatches,
} from '@app/utils/sidebar';
import OfferEvent from '@core/models/offer/OfferEvent';
import { EventCategory } from '@models/offer/enums';
import Sport from '@models/struct/Sport';
import {
    getTimeFilterFromSlug,
    getTimeFilterFunctionForTimeFilter,
} from '@shared/timeFilter/helpers';
import { TimeFilter } from '@shared/timeFilter/types';
import { getEventByTournamentFilter } from '@sportOfferFederation/competitions/virtual-tournaments/filterUtilities';
import { shellLocalConfig } from '@src/config/shell/localConfig';
import StoreUtil from '@store/StoreUtil';
import { RelativeRootGetters } from '@store/types';

import {
    PredefinedItems,
    ItemType,
} from './enums';
import { ISportsState } from './sportsIndex';
import { SelectedTreeItem } from './types';
import {
    PROMOTIONS_ID,
    PINNED_ID,
    ALL_ID,
} from '../constants';
import { getTimeFilterFromEvent } from '@shared/timeFilter/getTimeFilterFromEvent';
import { getSportIconName } from '@utils/sportsOffer/sportIconMap';
import {
    isDateBetweenInclusive,
    isValidDate,
    toDate,
} from '@superbet-group/web.lib.utilities';
import BookieDate from '@core/utils/date/BookieDate';
import { sortBy } from '@core/utils/sort/sortBy';
import { replaceTournamentsWithVirtualTournaments } from '@sportOfferFederation/competitions/utilities/sidebar';

export const selectedTreeItems = StoreUtil.createSimpleGetter('selectedTreeItems');

export const selectedFeaturedItem = StoreUtil.createSimpleGetter('selectedFeaturedItem');

export function getTimeFilter(rootState: IState) {
    const timeFilterSlug = rootState.route.params.timeFilter;
    return getTimeFilterFromSlug(timeFilterSlug);
}

// filter = sport, category, tournament
// suggest better name
/**
 * Maps URL to breadcrumb of ids.
 *
 * This getter takes slugs and maps slugs to ids (from tree for some date, not from struct!).
 * If someone comes from outside with offer/football/anglia method first finds id of football
 * and id of anglia.
 *
 * Example: if path is offer/football/anglia breadcrumb is
 * football > anglia and getter returns [5, 42]
 */
export function breadcrumbsAsIds(state: ISportsState, rootState: IState, rootGetters: RelativeRootGetters): number[] {
    const currentTree = tree(state, rootState, rootGetters);
    const currentRoute = rootState.route;
    return getBreadcrumbIds(currentRoute, currentTree, rootState);
}

export function filteredPromotionalEvents(state: ISportsState, rootState: IState, rootGetters: RelativeRootGetters) {
    const timeFilter = rootGetters['navigation/timeFilter'];

    if (!shellLocalConfig.defaultPromotionsTimeFilter.includes(timeFilter)
        || rootGetters['navigation/isOnSportLivePage']
        // eslint-disable-next-line sonarjs/no-duplicate-string
        || rootGetters['data/maintenance/isInMaintenance'](MaintenanceType.Prematch)
        || !!selectedFeaturedItem(state)) {
        return [];
    }

    const filters: Array<(match: OfferEvent) => boolean>
        = [(event: OfferEvent) => !event.isFinished() && !event.isStopped() && !event.isOngoing()];

    filters.push(getTimeFilterFunctionForTimeFilter(timeFilter));

    const promotionalEventGroups = rootGetters['data/sportOffer/promotionalEvents'];
    const filterFunc = (event: OfferEvent) => filters.every((func) => func(event));
    const filteredPromotionGroups = promotionalEventGroups.map((promotion) =>
        ({
            ...promotion,
            events: promotion.events.filter(filterFunc),
        }),
    ).filter((p) => p.events.length);

    return _sortBy(filteredPromotionGroups, 'order');
}

export function topTenEvents(state: ISportsState, rootState: IState, rootGetters: any) {
    const timeFilter = rootGetters['navigation/timeFilter'];

    if (!shellLocalConfig.defaultTopTenTimeFilter.includes(timeFilter)
        || rootGetters['navigation/isOnSportLivePage']
        || rootGetters['data/maintenance/isInMaintenance'](MaintenanceType.Prematch)) {
        return [];
    }

    const filters = [(event: OfferEvent) => !event.isFinished() && !event.isStopped() && !event.isOngoing()];

    return rootGetters['data/sportOffer/topTenEvents']
        .filter((event: OfferEvent) => filters.every((func) => func(event)));
}

export function getSuperLiveEvents(state: ISportsState, rootState: IState, rootGetters: any) {
    if (!(rootGetters['navigation/isOnSportLivePage'] || rootGetters['navigation/isOnHomePage'])
        || rootGetters['data/maintenance/isInMaintenance'](MaintenanceType.Live)
    ) {
        return [];
    }

    const liveEvents: OfferEvent[] = rootGetters['data/sportOffer/superLiveEvents']
        .filter(
            (event: OfferEvent) => !event.isFinished() && !event.isStopped() && event.isOngoing(),
        );

    return sortBy(liveEvents, [
        'matchPromotedLiveOrder',
        ...shellLocalConfig.sportOfferSort.getEventSortRules(),
    ]);
}

/**
 * This getter creates and updates sports sidebar tree from events filtered by some date
 * + previously selected breadcrumbs if date doesn't contain items in breadcrumbs or only some items
*/
// eslint-disable-next-line sonarjs/cognitive-complexity
export function tree(state: ISportsState, rootState: IState, rootGetters: RelativeRootGetters): INode[] {
    if (!rootGetters['navigation/isOnSportLivePage']) {
        const result: INode[] = [];

        rootState.data.sportOffer.sportsWithEvents.forEach((sportId) => {
            const sport = rootState.data.sportOffer.sports[sportId as number];
            if (sport) {
                result.push({
                    id: sport.id,
                    name: sport.name,
                    order: sport.order as number,
                    icon: `sds-icon-${getSportIconName(sport.id)}`,
                    children: [],
                    eventCount: 0,
                    urlName: sport.urlName,
                });
            }
        });
        return _sortBy(result, 'order');
    }

    const events = eventsFilteredByDate(state, rootState, rootGetters);
    const updatedEvents = replaceTournamentsWithVirtualTournaments(
        events,
        rootGetters['data/competitions/tournamentToVirtualTournament'],
    );
    const currentTree = buildTreeFromMatches(updatedEvents, {
        sports: rootState.data.sportOffer.sports,
        categories: rootState.data.sportOffer.categories,
        tournaments: rootState.data.sportOffer.tournaments,
    });
    const orderedTree = orderTree(Object.values(currentTree));

    return [
        {
            id: ALL_ID,
            name: 'all sports',
            urlName: 'all sports',
            children: [],
            eventCount: events.length,
            order: -2,
            icon: 'icon-sports_a-z',
        },
        ...orderedTree.map((item) => {
            // eslint-disable-next-line no-param-reassign
            item.children = [];
            // eslint-disable-next-line no-param-reassign
            item.icon = `sds-icon-${getSportIconName(item.id)}`;
            return item;
        },
        ),
    ];
}

export function sportSwitcherTree(rootState: IState, events: OfferEvent[]): Pick<INode, 'id' | 'name' | 'urlName' | 'icon'>[] {
    const tree: Record<number, INode> = {};
    const struct = {
        sports: rootState.data.sportOffer.sports,
        categories: rootState.data.sportOffer.categories,
        tournaments: rootState.data.sportOffer.tournaments,
    };

    events.forEach((event) => {
        createOrUpdateTreeNode(
            {
                id: event.sportId,
                name: event.sportName || _get(struct, ['sports', event.sportId, 'name']),
                urlName: _get(struct, ['sports', event.sportId, 'urlName']),
                children: {},
                eventCount: 0,
                order: event.sportOrder,
            },
            tree,
        );
    });

    return orderTree(Object.values(tree)).map((node) => ({
        id: node.id,
        name: node.name,
        urlName: node.urlName,
        icon: getSportIconName(node.id),
    }));
}

/**
 * Uses date from dateTimeFilter and returns all events with that date.
 */
export function eventsFilteredByDate(
    state: ISportsState,
    rootState: IState,
    rootGetters: RelativeRootGetters,
    excludeOutrightEvents = true,
): OfferEvent[] {
    const filters: Array<(match: OfferEvent) => boolean> = [
        (e: OfferEvent) => !e.isFinished() && !e.isStopped(),
    ];

    if (excludeOutrightEvents) {
        filters.push(excludeOutrights);
    }

    if (rootGetters['data/maintenance/isInMaintenance'](MaintenanceType.Live) || rootGetters['data/maintenance/isInMaintenance'](MaintenanceType.Prematch)) {
        filters.push((e: OfferEvent) => {
            const liveEventInMaintenance = e.isOngoing() && rootGetters['data/maintenance/isInMaintenance'](MaintenanceType.Live);
            const sportEventInMaintenance = !e.isOngoing() && rootGetters['data/maintenance/isInMaintenance'](MaintenanceType.Prematch);

            return !liveEventInMaintenance && !sportEventInMaintenance;
        });
    }
    const event = rootGetters['ui/sportOffer/expandedEventData'];
    const isOnOfferEventPage = rootGetters['navigation/isOnOfferEventPage'];

    const timeFilter = rootState.route.params.timeFilter;
    if (timeFilter && isValidDate(toDate(timeFilter))) {
        const desiredDate = toDate(timeFilter);
        const startDate = BookieDate.getStartDateForDate(desiredDate);
        const endDate = BookieDate.getEndDateForDate(desiredDate);
        filters.push((event: OfferEvent) => {
            const betTime = event.date;
            return isDateBetweenInclusive(startDate, endDate, betTime);
        });
    } else {
        const timeFilter = event && isOnOfferEventPage ? getTimeFilterFromEvent(event) : rootGetters['navigation/timeFilter'];

        if (timeFilter !== TimeFilter.Live) {
            filters.push((m: OfferEvent) => !m.isOngoing());
        }
        filters.push(getTimeFilterFunctionForTimeFilter(timeFilter));
    }
    const events = Object.values(rootState.data.sportOffer.events);
    return events.filter((event: OfferEvent) => filters.every((isValid) => isValid(event)));
}

/**
 * This getter takes treeBreadcrumbsIds and returns SelectedTreeItem object
 * with type (sport, tournament, category) and id so we can.  Events are going
 * to be filtered only by one type, the rightmost in breadcrumbs
 */
export function selectedItem(
    state: ISportsState,
    rootState: IState,
    rootGetters: RelativeRootGetters,
): SelectedTreeItem {
    const breadcrumbIds = breadcrumbsAsIds(state, rootState, rootGetters);

    const _selectedItem = {
        type: PredefinedItems.all,
        id: ALL_ID,
    } as SelectedTreeItem;

    if (breadcrumbIds[0] === PROMOTIONS_ID) {
        _selectedItem.type = PredefinedItems.promotions;
    } else if (breadcrumbIds[0] === PINNED_ID) {
        _selectedItem.type = PredefinedItems.pinnedEvents;
    // eslint-disable-next-line sonarjs/no-collapsible-if
    } else if (breadcrumbIds[0] !== ALL_ID) {
        if (breadcrumbIds.length > 0) {
            switch (breadcrumbIds.length) {
                case 3:
                    _selectedItem.type = ItemType.tournamentId;
                    break;
                case 2:
                    _selectedItem.type = ItemType.categoryId;
                    break;
                case 1:
                    _selectedItem.type = ItemType.sportId;
                    break;
            }
            _selectedItem.id = _last(breadcrumbIds)!;
        }
    }
    return _selectedItem;
}

function selectedFilterItem(state: ISportsState, rootState: IState, rootGetters: RelativeRootGetters) {
    const selectedFeatureItem = selectedFeaturedItem(state);
    if (selectedFeatureItem) {
        const filterIds: SelectedTreeItem[] = selectedFeatureItem.ids;
        return filterIds[filterIds.length - 1];
    }
    return selectedItem(state, rootState, rootGetters);
}

/**
 * This getter returns events for rendering, events are filtered by date from dateTimeFilter
 * and all the other filters (sport, competition, etc.)
 */
export function filteredEvents(state: ISportsState, rootState: IState, rootGetters: RelativeRootGetters): OfferEvent[] {
    const currentTreeItem = selectedFilterItem(state, rootState, rootGetters);
    if (rootState.route.params.sportSlug === PredefinedItems.promotions
        || currentTreeItem.type === PredefinedItems.pinnedEvents) {
        return [];
    }

    let events = eventsFilteredByDate(state, rootState, rootGetters);

    if (currentTreeItem.type !== PredefinedItems.all) {
        return events.filter((event: OfferEvent) => {
            return (event as any)[currentTreeItem.type] === currentTreeItem.id;
        });
    }
    return events;
}

/**
 * This getter returns events for rendering, events are filtered by date from dateTimeFilter
 * and all the other filters (sport, competition, etc.)
 */
export function competitionEvents(rootState: IState, rootGetters: RelativeRootGetters): OfferEvent[] {
    const currentTournamentId = rootGetters['ui/competitions/currentTournamentId'];
    const currentVirtualTournament = rootGetters['data/competitions/currentVirtualTournament'];
    if (!currentTournamentId && !currentVirtualTournament) {
        return [];
    }

    const filters: Array<(match: OfferEvent) => boolean> = [
        (event) => !event.isFinished() && !event.isStopped(),
        getEventByTournamentFilter(currentTournamentId, currentVirtualTournament),
    ];

    filters.push(getTimeFilterFunctionForTimeFilter(TimeFilter.All));

    filters.push(excludeOutrights);

    const events = Object.values(rootState.data.sportOffer.events);
    return events.filter((event: OfferEvent) => filters.every((isValid) => isValid(event)));
}

// mobile only

export function sportsFilter(state: ISportsState, rootState: IState, rootGetters: RelativeRootGetters): {
    id: Sport['id'];
    label: Sport['name'];
}[] {
    const allNavItem = {
        label: PredefinedItems.all,
        id: ALL_ID,
        icon: `icon-sport-${ALL_ID}`,
    };
    const events = eventsFilteredByDate(state, rootState, rootGetters);
    const sports = flow(
        uniqBy('sportId'),
        orderBy('sportOrder')('asc'),
        map(({ sportName, sportId }: any) => ({ label: sportName, id: sportId, icon: `icon-sport-${sportId}` })),
    )(events as any[]);
    sports.unshift(allNavItem);
    return sports;
}

function excludeOutrights(e: OfferEvent): boolean {
    return e.eventCategory !== EventCategory.Outright;
}
